import { useEffect, useRef } from 'react';
import { sendMessageToNative } from '../lib/sendMessageToNative';
import type { IBackHandler, IUseBackButtonOptions } from '../types';

const backHandlers: IBackHandler[] = [];

// Module-level variable to track logout history cutoff.
let logoutHistoryCutoff = 1;

/**
 * Sets the history cutoff point for logout.
 * Mobile hardware back button presses will trigger exit after this point.
 */
export const setLogoutHistoryCutoff = (historyLength: number): void => {
  logoutHistoryCutoff = historyLength;
};

/**
 * Processes the back button press by finding the highest priority handler that can handle back.
 * Sends a message to the native app to notify it that the back button was handled or not.
 */
export const processBackButton = (): void => {
  // Sort handlers by priority (highest first).
  const sortedHandlers = [...backHandlers].sort((a, b) => b.priority - a.priority);

  // Find the first handler that can handle back.
  const handler = sortedHandlers.find((h) => h.canHandleBack());

  if (handler) {
    // Execute the handler and notify native app that back was handled.
    handler.onBackPressed();
    sendMessageToNative('BACK_BUTTON_HANDLED', 'true');
  } else {
    // Notify native app that back was not handled.
    sendMessageToNative('BACK_BUTTON_HANDLED', 'false');
  }
};

/**
 * Hook for handling back button presses in React components.
 * This must be included at least once in the app, typically in a long-lived layout component,
 * to handle regular page navigation back button presses.
 *
 * It can be used in other places, when the back button is needed for other actions,
 * like closing a pop-up, as in the example below.
 *
 * @param options - The options for the back button bridge.
 * @param options.canHandleBack - A function that returns true if the back button should be handled.
 * @param options.onBackPressed - A function that will be called when the back button is pressed.
 * @param options.priority - The priority of the back button handler.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const [showModal, setShowModal] = useState(false);
 *
 *   // This will close the modal when back is pressed if it's open
 *   useBackButtonBridge({
 *     canHandleBack: () => showModal,
 *     onBackPressed: () => setShowModal(false),
 *     priority: 10, // Higher priority than default navigation
 *   });
 *
 *   return (
 *     <div>
 *       {showModal && <Modal onClose={() => setShowModal(false)} />}
 *       <button onClick={() => setShowModal(true)}>Open Modal</button>
 *     </div>
 *   );
 * };
 * ```
 */
export const useBackButtonBridge = ({
  canHandleBack = () => window.history.length > logoutHistoryCutoff,
  onBackPressed = () => window.history.back(),
  priority = 0,
}: IUseBackButtonOptions = {}) => {
  // Use references to the callbacks to avoid unnecessary re-registrations.
  const canHandleBackRef = useRef(canHandleBack);
  const onBackPressedRef = useRef(onBackPressed);
  canHandleBackRef.current = canHandleBack;
  onBackPressedRef.current = onBackPressed;

  // Register/unregister this component's back handler.
  useEffect(() => {
    // Create a unique ID for this handler.
    const id = Symbol('backHandler');

    // Register this handler.
    const handler: IBackHandler = {
      id,
      canHandleBack: () => canHandleBackRef.current(),
      onBackPressed: () => onBackPressedRef.current(),
      priority,
    };

    backHandlers.push(handler);

    // Cleanup on unmount.
    return () => {
      const index = backHandlers.findIndex((h) => h.id === id);
      if (index !== -1) {
        backHandlers.splice(index, 1);
      }
    };
  }, [priority]);
};
